/**
 * LpDiligencePanel — combined LP-diligence view for a GP/investor.
 *
 *   - Form ADV summary (regulatory + AUM + fee structure + custody)
 *   - Form D fund history (vehicles raised, vintages, sizes)
 *   - Co-investor graph (top names sharing filings)
 *   - Portfolio outcomes (counts + exit rate + named exits)
 *   - Uploaded performance docs (LP letters etc.) with extracted IRR/TVPI/DPI/MOIC
 *   - Upload slot for new performance docs
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Loader2, RefreshCw, FileText, Upload, ExternalLink, AlertTriangle,
  TrendingUp, Building2, Briefcase,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

interface FormAdv {
  crd?: string | null;
  firm_name?: string | null;
  sec_number?: string | null;
  is_active?: boolean;
  has_disciplinary_history?: boolean;
  regulatory_aum_usd?: number | null;
  discretionary_aum_usd?: number | null;
  employees?: number | null;
  advisers?: number | null;
  private_fund_count?: number | null;
  private_fund_aum_usd?: number | null;
  custody?: boolean | null;
  fee_types?: string[];
  report_pdf_url?: string | null;
  fetched_at?: string | null;
}

interface FormDFiling {
  accession_no: string;
  issuer_name: string | null;
  industry_group: string | null;
  total_offering_amount: number | null;
  total_amount_sold: number | null;
  total_investor_count: number | null;
  date_of_first_sale: string | null;
  filed_at: string | null;
  filing_index_url: string | null;
}

interface CoInvestor {
  full_name: string;
  investor_id: number | null;
  shared_filings: number;
}

interface PortfolioOutcomes {
  counts: Record<string, number>;
  total: number;
  exit_rate_pct: number;
  exits: Array<{
    company_name: string;
    exit_type: string | null;
    investment_date: string | null;
    exit_multiple: number | null;
  }>;
}

interface PerfDoc {
  id: number;
  doc_type: string | null;
  fund_name: string | null;
  fund_vintage: number | null;
  fund_size_usd: number | null;
  irr_net: number | null;
  irr_gross: number | null;
  tvpi: number | null;
  dpi: number | null;
  rvpi: number | null;
  moic: number | null;
  realized_proceeds_usd: number | null;
  unrealized_value_usd: number | null;
  lp_count: number | null;
  reporting_date: string | null;
  extracted_at: string | null;
  extraction_confidence: number | null;
  original_filename: string | null;
}

interface LpDiligencePayload {
  investor_id: number;
  investor_name: string | null;
  firm_name: string | null;
  form_adv: FormAdv | null;
  form_d_fund_history: FormDFiling[];
  form_d_filing_count: number;
  co_investors: CoInvestor[];
  portfolio_outcomes: PortfolioOutcomes;
  performance_docs: PerfDoc[];
}

interface Props {
  investorId: number;
}

const fmtUsd = (n?: number | null): string => {
  if (n === undefined || n === null) return '—';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const fmtPct = (n?: number | null): string => {
  if (n === undefined || n === null) return '—';
  return `${n.toFixed(1)}%`;
};

const fmtMultiple = (n?: number | null): string => {
  if (n === undefined || n === null) return '—';
  return `${n.toFixed(2)}×`;
};

const LpDiligencePanel: React.FC<Props> = ({ investorId }) => {
  const [data, setData] = useState<LpDiligencePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshingAdv, setRefreshingAdv] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/investors/${investorId}/lp-diligence`);
      setData(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to load LP diligence');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [investorId]);

  const refreshAdv = async () => {
    setRefreshingAdv(true);
    try {
      await api.post(`/investors/${investorId}/form-adv/refresh`);
      toast.success('Form ADV refreshed');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Form ADV refresh failed');
    } finally {
      setRefreshingAdv(false);
    }
  };

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/investors/${investorId}/performance-docs`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Performance doc extracted and saved');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-6 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading LP diligence…
      </div>
    );
  }
  if (!data) return null;

  const adv = data.form_adv;
  const oc = data.portfolio_outcomes;

  return (
    <div className="space-y-4">
      {/* Form ADV */}
      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-600" />
            Form ADV (regulatory)
          </h2>
          <button
            onClick={refreshAdv}
            disabled={refreshingAdv}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-60"
          >
            {refreshingAdv
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
        {adv ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="CRD" value={adv.crd || '—'} />
            <Stat label="SEC #" value={adv.sec_number || '—'} />
            <Stat label="Status" value={adv.is_active ? 'Active' : 'Historical'} />
            <Stat
              label="Disciplinary"
              value={adv.has_disciplinary_history ? 'Disclosed' : 'None on file'}
              warn={!!adv.has_disciplinary_history}
            />
            <Stat label="Reg. AUM" value={fmtUsd(adv.regulatory_aum_usd)} />
            <Stat label="Discretionary AUM" value={fmtUsd(adv.discretionary_aum_usd)} />
            <Stat label="Employees" value={adv.employees ?? '—'} />
            <Stat label="Advisers" value={adv.advisers ?? '—'} />
            <Stat label="Private funds" value={adv.private_fund_count ?? '—'} />
            <Stat label="Private fund AUM" value={fmtUsd(adv.private_fund_aum_usd)} />
            <Stat
              label="Custody"
              value={adv.custody === null || adv.custody === undefined
                ? '—'
                : adv.custody ? 'Yes' : 'No'}
            />
            <Stat label="Fee types" value={(adv.fee_types || []).join(', ') || '—'} />
            {adv.report_pdf_url && (
              <a
                href={adv.report_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="col-span-2 md:col-span-4 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
              >
                <FileText className="w-3 h-3" />
                Open Form ADV PDF
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No Form ADV cached yet. Click <em>Refresh</em> to fetch from SEC IAPD.
          </p>
        )}
      </section>

      {/* Form D fund history */}
      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
          Form D fund history ({data.form_d_filing_count})
        </h2>
        {data.form_d_fund_history.length === 0 ? (
          <p className="text-sm text-gray-500">
            No Form D filings link to this investor yet. Run the Form D
            linker after the next ingest, or backfill from the Railway shell.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="text-left py-1.5 pr-3">Filed</th>
                  <th className="text-left py-1.5 pr-3">Issuer</th>
                  <th className="text-left py-1.5 pr-3">Industry</th>
                  <th className="text-right py-1.5 pr-3">Offering</th>
                  <th className="text-right py-1.5 pr-3">Sold</th>
                  <th className="text-right py-1.5 pr-3">Investors</th>
                  <th className="text-left py-1.5 pr-3">First sale</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.form_d_fund_history.map((f) => (
                  <tr key={f.accession_no} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-1.5 pr-3">
                      {f.filed_at ? new Date(f.filed_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-1.5 pr-3">{f.issuer_name || '—'}</td>
                    <td className="py-1.5 pr-3 text-xs text-gray-600">
                      {f.industry_group || '—'}
                    </td>
                    <td className="py-1.5 pr-3 text-right">{fmtUsd(f.total_offering_amount)}</td>
                    <td className="py-1.5 pr-3 text-right">{fmtUsd(f.total_amount_sold)}</td>
                    <td className="py-1.5 pr-3 text-right">{f.total_investor_count ?? '—'}</td>
                    <td className="py-1.5 pr-3">{f.date_of_first_sale || '—'}</td>
                    <td className="py-1.5 pr-3">
                      {f.filing_index_url && (
                        <a
                          href={f.filing_index_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:underline"
                        >
                          <ExternalLink className="w-3 h-3 inline" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Portfolio outcomes */}
      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <Briefcase className="w-4 h-4 text-indigo-600" />
          Portfolio outcomes (inferred)
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
          <Stat label="Total" value={oc.total} />
          <Stat label="Active" value={oc.counts.active ?? 0} />
          <Stat label="Follow-on" value={oc.counts.follow_on ?? 0} />
          <Stat label="Exited" value={oc.counts.exited ?? 0} />
          <Stat label="Failed" value={oc.counts.failed ?? 0} />
          <Stat label="Exit rate" value={fmtPct(oc.exit_rate_pct)} />
        </div>
        {oc.exits.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
              Notable exits
            </div>
            <ul className="text-sm space-y-0.5">
              {oc.exits.map((e, i) => (
                <li key={i} className="text-gray-700">
                  • {e.company_name}
                  {e.exit_type && <span className="text-gray-500"> — {e.exit_type}</span>}
                  {e.investment_date && (
                    <span className="text-gray-400"> ({e.investment_date})</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Co-investors */}
      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">
          Top co-investors (from shared Form D filings)
        </h2>
        {data.co_investors.length === 0 ? (
          <p className="text-sm text-gray-500">No co-investor signal yet.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {data.co_investors.map((c, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="text-gray-800">{c.full_name}</span>
                <span className="text-xs text-gray-500">
                  {c.shared_filings} shared filing{c.shared_filings === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Performance docs */}
      <section className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700">
            Performance docs (LP letters / capital account / quarterly)
          </h2>
          <label className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 cursor-pointer">
            {uploading
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Upload className="w-3 h-3" />}
            Upload PDF
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </label>
        </div>
        {data.performance_docs.length === 0 ? (
          <p className="text-sm text-gray-500">
            No docs uploaded yet. Drop in an LP letter or capital account
            statement and the extractor will pull IRR, TVPI, DPI, MOIC,
            and reporting date.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="text-left py-1.5 pr-3">Reporting date</th>
                  <th className="text-left py-1.5 pr-3">Fund</th>
                  <th className="text-left py-1.5 pr-3">Doc type</th>
                  <th className="text-right py-1.5 pr-3">IRR (net)</th>
                  <th className="text-right py-1.5 pr-3">TVPI</th>
                  <th className="text-right py-1.5 pr-3">DPI</th>
                  <th className="text-right py-1.5 pr-3">MOIC</th>
                  <th className="text-right py-1.5 pr-3">LPs</th>
                </tr>
              </thead>
              <tbody>
                {data.performance_docs.map((d) => (
                  <tr key={d.id} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-1.5 pr-3">{d.reporting_date || '—'}</td>
                    <td className="py-1.5 pr-3">
                      {d.fund_name || '—'}
                      {d.fund_vintage && (
                        <span className="text-gray-400"> ({d.fund_vintage})</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-xs text-gray-600">{d.doc_type || '—'}</td>
                    <td className="py-1.5 pr-3 text-right">{fmtPct(d.irr_net)}</td>
                    <td className="py-1.5 pr-3 text-right">{fmtMultiple(d.tvpi)}</td>
                    <td className="py-1.5 pr-3 text-right">{fmtMultiple(d.dpi)}</td>
                    <td className="py-1.5 pr-3 text-right">{fmtMultiple(d.moic)}</td>
                    <td className="py-1.5 pr-3 text-right">{d.lp_count ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const Stat: React.FC<{
  label: string;
  value: string | number;
  warn?: boolean;
}> = ({ label, value, warn }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-sm font-medium ${warn ? 'text-red-700' : 'text-gray-900'}`}>
      {warn && <AlertTriangle className="w-3 h-3 inline mr-1 text-red-500" />}
      {value}
    </div>
  </div>
);

export default LpDiligencePanel;
